/**
 * career-applications — the applications BOARD, with a restrained way to start a new one
 * (route `/applications`).
 *
 * S11 (2026-07-29) turned this surface opportunity-first. It used to open with a prominent
 * picker listing EVERY opportunity the person had ever discovered — dismissed ones included —
 * above a board that came last. Three things changed, all client-side:
 *
 *   1. **The board leads.** {@link CareerApplications.pipeline} renders directly under the hero:
 *      the work already in flight is what the person came for.
 *   2. **The plus is restrained.** A native `<details>` disclosure (the app's established idiom)
 *      composed from the R2 action primitives — it opens a chooser instead of occupying the
 *      surface with one.
 *   3. **The chooser is scoped to what applying actually means:** the BOOKMARKED opportunities
 *      that do not have an application yet — `opportunityStore.list("gemerkt")` minus every
 *      `Application.opportunityRef`. The German copy has promised exactly this since the surface
 *      shipped (»Finde und merke dir zuerst eine Stelle …«); the code now keeps the promise.
 *      Both signals were already in scope — no new endpoint, no server change.
 *
 * Also here: the owner's applications (fetch-reconciled from `/api/applications`) and the LIVE
 * push rail (the GenerationRun store folded from the push plane, `role="log" aria-live="polite"`,
 * stage + review-round counts with meaning). Each application card carries its `is-<status>` state
 * and an `open-application` verb. All chrome flows through `t()`; letter/kurzprofil CONTENT is
 * domain data.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Router,
  type Signal,
  type Urn,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { skeletonApplicationCards } from "../lib/skeleton"
import { hubHero, HUB_TITLE_ID } from "../lib/hub"
import { readLastGeneration, writeLastGeneration } from "../lib/last-generation"
import { action, actionBody, actionClass } from "../lib/action"
import {
  ApplicationStoreToken,
  GenerationRunStoreToken,
  I18nToken,
  OpportunityStoreToken,
  PushToken,
  RouterToken,
  SessionToken,
  apiGet,
  apiPost,
} from "../services"
import { createStreamClaim, type StreamClaim } from "../lib/push-streams"
import type { CareerI18n, CareerKey } from "../../i18n"
import {
  applicationStatusClass,
  careerStream,
  type ApplicationStore,
  type ApplicationView,
  type GenerationRunStage,
  type GenerationRunStatus,
  type GenerationRunStore,
  type GenerationRunView,
  type OpportunityCardView,
  type OpportunityStore,
  type ShellSession,
} from "../domain"

/** One row of `GET /api/opportunities` — the folded opportunity, its match, its org digest. */
interface OpportunityEnvelope {
  opportunity: Record<string, unknown>
  match?: unknown
  org?: Record<string, unknown>
}
interface GenerateResponse {
  applicationId: string
  generationId: string
  streamId: string
  applicationStreamId: string
}

/** The bare id an opportunity URN addresses (`urn:career:opportunity:o1` → `o1`). */
const shortOpportunityId = (urn: string): string => urn.replace(/^urn:career:opportunity:/, "")

const STAGE_KEY: Record<GenerationRunStage, CareerKey> = {
  preselecting: "applications.gen.stage.preselecting",
  authoring: "applications.gen.stage.authoring",
  judging: "applications.gen.stage.judging",
  kurzprofil: "applications.gen.stage.kurzprofil",
  tailoring: "applications.gen.stage.tailoring",
  emailing: "applications.gen.stage.emailing",
  done: "applications.gen.stage.done",
}
const STATUS_KEY: Record<GenerationRunStatus, CareerKey> = {
  running: "applications.gen.status.running",
  completed: "applications.gen.status.completed",
  stopped: "applications.gen.status.stopped",
  failed: "applications.gen.status.failed",
}
const APP_STATUS_KEY: Record<string, CareerKey> = {
  draft: "applications.status.draft",
  in_review: "applications.status.in_review",
  changes_requested: "applications.status.changes_requested",
  approved: "applications.status.approved",
  sent: "applications.status.sent",
}
/** The pipeline column order (draft → in review → changes → approved → sent) — the real lifecycle. */
const PIPELINE_ORDER = ["draft", "in_review", "changes_requested", "approved", "sent"] as const

const POLL_MS = 2500

export class ApplicationsVm implements ViewModel {
  readonly applications: ReadonlySignal<readonly ApplicationView[]>
  readonly runs: ReadonlySignal<readonly GenerationRunView[]>
  /**
   * The BOOKMARKED opportunities (`o.bookmarked && !o.dismissed`, newest first) — the
   * ready-made `gemerkt` projection the search surface already renders. Bookmarking is the
   * person's own »die will ich« verdict, which is exactly the population an application
   * chooser may draw from. See {@link choosable} for the minus-set.
   */
  readonly bookmarked: ReadonlySignal<readonly OpportunityCardView[]>
  readonly selected: Signal<string | null> = signal<string | null>(null)
  readonly generating: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  private readonly streams = new Set<string>()
  private seq = 1
  private timer: ReturnType<typeof setInterval> | null = null

  /** This surface's claim on the shared push channel (`lib/push-streams.ts`): the channel
   *  subscription is the UNION of every mounted surface's claim, never just ours. */
  private readonly claim: StreamClaim

  constructor(
    private readonly store: ApplicationStore,
    private readonly genStore: GenerationRunStore,
    private readonly oppStore: OpportunityStore,
    push: PushChannel,
    private readonly router: Router,
    private readonly session: ShellSession,
    i18n: CareerI18n,
  ) {
    this.t = i18n.t
    this.claim = createStreamClaim(push)
    this.applications = store.list("alle")
    this.runs = genStore.list("aktiv")
    this.bookmarked = oppStore.list("gemerkt")
    this.renderDeps = [this.applications, this.runs, this.bookmarked, this.selected, this.generating, this.error, this.loaded, store.pending]
  }

  /**
   * The opportunity URNs that already HAVE an application — the chooser's minus-set.
   *
   * `Application.opportunityRef` is required by the domain but typed optional on the client
   * view (`domain.ts`), so a wire shape that predates the field folds to `undefined`. Skipping
   * those is the tolerant read: an application we cannot attribute must never silently swallow
   * an opportunity the person can still apply for.
   */
  appliedOpportunityRefs(): ReadonlySet<string> {
    const refs = new Set<string>()
    for (const { opportunityRef } of this.applications.value) {
      if (typeof opportunityRef === "string" && opportunityRef.length > 0) refs.add(opportunityRef)
    }
    return refs
  }

  /** What a NEW application can actually be written for: bookmarked MINUS already-applied. */
  choosable(): readonly OpportunityCardView[] {
    const applied = this.appliedOpportunityRefs()
    return this.bookmarked.value.filter((o) => !applied.has(o.id))
  }

  private get userId(): string | null {
    return this.session.user?.sub ?? null
  }

  attach(): () => void {
    void this.hydrate()
    this.resumeLastGeneration()
    return () => {
      this.stopPolling()
      this.claim.release()
    }
  }

  /**
   * Re-open the streams of the last generation this browser started (see
   * `lib/last-generation.ts`). The push plane REPLAYS both streams on subscribe, so a run
   * that finished — or failed — while the surface was away folds straight back into the rail
   * instead of leaving it empty on a cold load. Polling reconciles the durable applications
   * and self-stops as soon as nothing is running any more.
   */
  private resumeLastGeneration(): void {
    const last = readLastGeneration(this.userId)
    if (!last) return
    const fresh = [last.streamId, last.applicationStreamId].filter((s) => s.length > 0 && !this.streams.has(s))
    if (fresh.length === 0) return
    for (const s of fresh) this.streams.add(s)
    this.claim.claim([...this.streams])
    this.startPolling()
  }

  isRunning(): boolean {
    return this.runs.value.some((r) => r.status === "running")
  }

  /** The generation run driving this application, if one is in the rail. */
  runFor(applicationId: string): GenerationRunView | undefined {
    return this.runs.value.find((r) => r.applicationRef === applicationId)
  }

  select(shortId: string): void {
    this.selected.set(shortId)
  }

  async generate(): Promise<void> {
    const shortId = this.selected.value
    if (!shortId) return
    this.generating.set(true)
    this.error.set(null)
    try {
      const res = await apiPost<GenerateResponse>("/api/applications/generate", { opportunityId: shortId })
      // Remember the two streams so a reload MID-run (or a re-login) can resume the rail.
      writeLastGeneration(this.userId, { streamId: res.streamId, applicationStreamId: res.applicationStreamId })
      this.streams.add(res.streamId)
      this.streams.add(res.applicationStreamId)
      this.claim.claim([...this.streams]) // live: gen rail + application content
      await this.hydrate()
      this.startPolling()
      // Jump straight to the review surface — the rail plays there too.
      const appShort = res.applicationId.replace(/^urn:career:application:/, "")
      void this.router.navigate(`/applications/${appShort}`)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.generating.set(false)
    }
  }

  go(url: string): void {
    void this.router.navigate(url)
  }

  private startPolling(): void {
    this.stopPolling()
    this.timer = setInterval(() => {
      void this.hydrate().then(() => {
        if (!this.isRunning()) this.stopPolling()
      })
    }, POLL_MS)
  }
  private stopPolling(): void {
    if (this.timer !== null) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  /**
   * Reconcile the owner's applications AND their opportunities into the shared stores.
   *
   * The opportunities are folded into the SAME `OpportunityStore` the search surface writes
   * (same synthetic-`Discovered` reconcile, mirroring `career-search`), so the `gemerkt`
   * projection is authoritative here too — a bookmark set on `/search` is already true on
   * this surface the moment it hydrates.
   */
  private async hydrate(): Promise<void> {
    const userId = this.userId
    try {
      const apps = await apiGet<Record<string, unknown>[]>("/api/applications")
      if (userId) {
        for (const app of apps) {
          const id = app["id"]
          if (typeof id !== "string") continue
          // Seed/refresh the store via a synthetic Drafted carrying the FULL folded application.
          this.store.apply({ streamId: careerStream(userId, "Application", id), version: this.seq++, type: "application.Drafted", payload: { owner: userId, application: app } })
        }
      }
      const opps = await apiGet<OpportunityEnvelope[]>("/api/opportunities")
      if (userId) {
        for (const { opportunity, match, org } of opps) {
          const id = opportunity["id"]
          if (typeof id !== "string") continue
          const streamId = careerStream(userId, "Opportunity", id)
          this.oppStore.apply({ streamId, version: this.seq++, type: "opportunity.Discovered", payload: { owner: userId, opportunity, ...(org ? { org } : {}) } })
          if (match) {
            this.oppStore.apply({ streamId, version: this.seq++, type: "opportunity.Matched", payload: { owner: userId, opportunityId: id, match } })
          }
        }
      }
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }
}

export class CareerApplications extends KitElement<ApplicationsVm> {
  protected createViewModel(ctx: KitContext): ApplicationsVm {
    return new ApplicationsVm(
      ctx.inject(ApplicationStoreToken),
      ctx.inject(GenerationRunStoreToken),
      ctx.inject(OpportunityStoreToken),
      ctx.inject(PushToken),
      ctx.inject(RouterToken),
      ctx.inject(SessionToken),
      ctx.inject(I18nToken),
    )
  }

  /**
   * The restrained plus (S11). A native `<details>` — the disclosure idiom this app already
   * uses everywhere — whose summary is the R2 `quiet` action chrome (composed via
   * {@link actionClass} / {@link actionBody}, because a `<summary>` IS the affordance and so
   * carries no `data-act` of its own). Closed, it is one quiet line under the board; open, it
   * is the chooser. It never competes with the applications for the eye.
   */
  private newApplication(vm: ApplicationsVm): TemplateResult {
    const choosable = vm.choosable()
    return html`
      <div class="applications-new">
        <details class="applications-new-disclosure">
          <summary class=${actionClass({ variant: "quiet", className: "applications-new-summary" })} lang="de">
            ${actionBody({ act: "new-application", label: vm.t("applications.new"), variant: "quiet", intent: "add" })}
          </summary>
          <div class="applications-new-body">
            <h2 class="surface-section-title" lang="de">${vm.t("applications.pick.title")}</h2>
            ${this.chooser(vm, choosable)}
          </div>
        </details>
        ${choosable.length > 0
          ? html`<span class="applications-new-count" lang="de">${vm.t("applications.pick.available", { n: choosable.length })}</span>`
          : nothing}
      </div>
    `
  }

  /**
   * The chooser: bookmarked opportunities MINUS the ones that already have an application.
   * Empty is not a dead end — it says WHICH of the two reasons it is, and offers the move that
   * fixes it. Rows are a catalog of comparable EXTERNAL items (§0.1 Genre B), so each draws the
   * `.card-item` edge; selection speaks through `--accent-edge` on the shared `.has-accent`
   * marker, never a fill.
   */
  private chooser(vm: ApplicationsVm, choosable: readonly OpportunityCardView[]): TemplateResult {
    if (choosable.length === 0) {
      const everythingApplied = vm.bookmarked.value.length > 0
      return html`
        <p class="surface-empty" lang="de">${vm.t(everythingApplied ? "applications.pick.allApplied" : "applications.pick.none")}</p>
        ${action({ act: "nav", label: vm.t("applications.pick.toSearch"), intent: "forward", payload: { url: "/search" } })}
      `
    }
    const selected = vm.selected.value
    return html`
      <div class="applications-picker" role="group" aria-label=${vm.t("applications.pick.title")}>
        <ul class="applications-pick-list" role="list">
          ${repeat(choosable, (o) => o.id, (o) => this.pickRow(vm, o, selected))}
        </ul>
        ${action({
          act: "generate-application",
          label: vm.t("applications.generate"),
          variant: "primary",
          className: "applications-generate-btn",
          payload: { opportunity: selected ?? "" },
          disabled: !selected,
          pending: vm.generating.value,
          pendingLabel: vm.t("applications.generating"),
        })}
      </div>
    `
  }

  /** One choosable Stelle — the posting first, the employer and place as its quiet second line. */
  private pickRow(vm: ApplicationsVm, o: OpportunityCardView, selected: string | null): TemplateResult {
    const shortId = shortOpportunityId(o.id)
    const where = [o.employer, o.location].filter((s): s is string => Boolean(s)).join(" · ")
    return html`
      <li>
        <button
          data-act="select-opportunity"
          data-opportunity=${shortId}
          class=${classMap({ "card-item": true, "has-accent": true, "applications-pick": true, "is-selected": selected === shortId })}
          aria-pressed=${selected === shortId}
          lang="de"
        >
          <span class="applications-pick-title" lang="de">${o.title}</span>
          ${where ? html`<span class="applications-pick-where" lang="de">${where}</span>` : nothing}
        </button>
      </li>
    `
  }

  /**
   * One row of the live rail — readable at a glance: a pulsing dot while the run works, the
   * failed run's own error as a quiet second line (nothing else tells the user WHY), and a way
   * into the application the run is writing.
   */
  private railRun(vm: ApplicationsVm, run: GenerationRunView): TemplateResult {
    const running = run.status === "running"
    const application = run.applicationRef.replace(/^urn:career:application:/, "")
    return html`
      <li class=${classMap({ "generation-run": true, [`is-${run.status}`]: true })} data-run-id=${run.urn}>
        ${running ? html`<span class="gen-dot" aria-hidden="true"></span>` : nothing}
        <span class="run-stage chip" lang="de">${vm.t(STAGE_KEY[run.stage])}</span>
        <span class="run-status" lang="de">${vm.t(STATUS_KEY[run.status])}</span>
        ${run.iterations > 0 ? html`<span class="run-iterations chip" lang="de">${vm.t("applications.gen.iterations", { n: run.iterations })}</span>` : nothing}
        ${run.applicationRef
          ? html`<button data-act="open-application" data-application=${application} class="run-open" lang="de">${vm.t("applications.open")}</button>`
          : nothing}
        ${run.status === "failed" && run.error ? html`<p class="run-error" lang="de">${vm.t("review.failed.detail", { message: run.error })}</p>` : nothing}
      </li>
    `
  }

  /**
   * One application, state-first: while its run works, the current stage carries the activity
   * dot; a failed run says so. The letter snippet and the gate score render ONLY once a real
   * letter has been authored — the server's seeded placeholder body is not a letter, and
   * quoting it (or scoring it 0/6) would be a lie about the state of the work.
   */
  private card(vm: ApplicationsVm, a: ApplicationView): TemplateResult {
    const passed = a.gates.filter((g) => g.pass).length
    const run = vm.runFor(a.id)
    return html`
      <li class=${classMap({ "application-card": true, [applicationStatusClass(a.status)]: true, "is-degraded": a.letterDegraded })} data-application=${a.urn} urn=${a.urn}>
        <p class="application-card-status chip" lang="de">${vm.t(APP_STATUS_KEY[a.status] ?? "applications.status.draft")}</p>
        ${run?.status === "running"
          ? html`<p class="application-card-stage chip" lang="de"><span class="gen-dot" aria-hidden="true"></span>${vm.t(STAGE_KEY[run.stage])}</p>`
          : nothing}
        ${run?.status === "failed" ? html`<p class="application-card-failed chip" lang="de">${vm.t("applications.gen.status.failed")}</p>` : nothing}
        ${a.letterAuthored && a.gates.length > 0 ? html`<p class="application-card-gates chip" lang="de">${passed}/${a.gates.length} · ${a.converged ? vm.t("applications.gen.converged") : vm.t("applications.gen.notConverged")}</p>` : nothing}
        ${a.letterAuthored && a.letterBody ? html`<p class="application-card-snippet" lang="de">${a.letterBody.split("\n\n")[0]}</p>` : nothing}
        <button data-act="open-application" data-application=${a.id.replace(/^urn:career:application:/, "")} class="application-open" lang="de">${vm.t("applications.open")}</button>
      </li>
    `
  }

  /** The state-derived hero: nothing yet → invite into the search; else the pipeline is summarized
   *  and the ONE primary CTA opens the most recent application. */
  private hero(vm: ApplicationsVm): TemplateResult {
    const apps = vm.applications.value
    const first = apps[0]
    if (!first) {
      return hubHero({
        eyebrow: vm.t("applications.hub.eyebrow"),
        title: vm.t("applications.hub.title.empty"),
        lead: vm.t("applications.hub.lead.empty"),
        cta: { label: vm.t("applications.hub.cta.search"), act: "nav", url: "/search" },
      })
    }
    return hubHero({
      eyebrow: vm.t("applications.hub.eyebrow"),
      title: vm.t("applications.hub.title.active", { n: apps.length }),
      lead: vm.t("applications.hub.lead.active"),
      cta: { label: vm.t("applications.hub.cta.open"), act: "nav", url: `/applications/${first.id.replace(/^urn:career:application:/, "")}` },
    })
  }

  /**
   * The pipeline board — applications grouped into lifecycle columns (only non-empty columns
   * show). S11 promoted it ABOVE the create-flow: what is already in flight leads the surface.
   *
   * Empty, it does NOT shove a picker at the reader — and it does not repeat the hero either.
   * Two cases:
   *   · saved Stellen ARE waiting → say how many and where the choice lives. That is real
   *     information the hero does not have, and it names the move without opening anything.
   *   · nothing saved yet → the hero above is ALREADY the empty state (»Noch keine
   *     Bewerbungen« + »Finde eine passende Stelle« + »Zur Suche«). A second paragraph saying
   *     the same thing in other words is noise, so this section renders nothing at all and the
   *     surface reads as one statement. The plus below still explains itself when opened.
   */
  private pipeline(vm: ApplicationsVm): TemplateResult | typeof nothing {
    const apps = vm.applications.value
    if (apps.length === 0) {
      const ready = vm.choosable().length
      if (vm.loaded.value && ready === 0) return nothing
      return html`<section class="applications-list-section" aria-labelledby="applications-list-title" aria-live="polite">
        <h2 id="applications-list-title" class="surface-section-title" lang="de">${vm.t("applications.list.title")}</h2>
        ${vm.loaded.value
          ? html`<p class="surface-empty" lang="de">${vm.t("applications.empty.ready", { n: ready })}</p>`
          : html`<ul class="application-list" role="list" aria-busy="true">${skeletonApplicationCards(3)}</ul>`}
      </section>`
    }
    const groups = PIPELINE_ORDER.map((status) => ({ status, items: apps.filter((a) => a.status === status) })).filter((g) => g.items.length > 0)
    return html`
      <section class="applications-list-section" aria-labelledby="applications-list-title" aria-live="polite">
        <h2 id="applications-list-title" class="surface-section-title" lang="de">${vm.t("applications.list.title")}</h2>
        <div class="hub-pipeline">
          ${groups.map(
            (g) => html`<div class=${classMap({ "hub-pipe-col": true, [applicationStatusClass(g.status)]: true })}>
              <div class="hub-pipe-head">
                <span class="hub-pipe-label" lang="de">${vm.t(APP_STATUS_KEY[g.status] ?? "applications.status.draft")}</span>
                <span class="hub-pipe-count chip" lang="de">${g.items.length}</span>
              </div>
              <ul class="application-list" role="list">${repeat(g.items, (a) => a.urn, (a) => this.card(vm, a))}</ul>
            </div>`,
          )}
        </div>
      </section>
    `
  }

  protected render(vm: ApplicationsVm): TemplateResult {
    const runs = vm.runs.value
    const err = vm.error.value
    return html`
      <section class="surface applications applications-hub" aria-labelledby=${HUB_TITLE_ID}>
        ${this.hero(vm)}
        ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}

        ${this.pipeline(vm)}
        ${this.newApplication(vm)}

        <section class="applications-rail-section" aria-labelledby="applications-rail-title">
          <h2 id="applications-rail-title" class="surface-section-title" lang="de">${vm.t("applications.rail.title")}</h2>
          <ol class="applications-rail" role="log" aria-live="polite" aria-label=${vm.t("applications.rail.title")}>
            ${runs.length === 0
              ? html`<li class="applications-rail-empty" lang="de">${vm.t("applications.rail.empty")}</li>`
              : repeat(runs, (r) => r.urn, (r) => this.railRun(vm, r))}
          </ol>
        </section>
      </section>
    `
  }

  protected override actions(vm: ApplicationsVm): ActionMap {
    return {
      "select-opportunity": ({ opportunity }) => {
        if (opportunity) vm.select(opportunity)
      },
      "generate-application": () => void vm.generate(),
      "open-application": ({ application }, event) => {
        event.preventDefault()
        if (application) vm.go(`/applications/${application}`)
      },
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
